import "./DesktopStatsSection.css";
import { useEffect, useMemo, useState } from "react";
import { useLang } from "../../../../i18n/LangContext";

const parseStat = (value) => {
  const raw = String(value || "").trim();
  const hasPlus = raw.endsWith("+");
  const trimmed = hasPlus ? raw.slice(0, -1) : raw;
  const prefixMatch = trimmed.match(/^[^\d]+/);
  const prefix = prefixMatch ? prefixMatch[0] : "";
  const unitMatch = trimmed.match(/[KMB]$/i);
  const unit = unitMatch ? unitMatch[0].toUpperCase() : "";
  const numberPart = trimmed.replace(/[^\d.]/g, "");
  const decimals = numberPart.includes(".") ? 1 : 0;
  const numeric = Number(numberPart) || 0;
  return { raw, prefix, unit, hasPlus, numeric, decimals };
};

const formatCount = (value, decimals) => {
  const fixed = value.toFixed(decimals);
  return decimals ? fixed.replace(/\.0$/, "") : fixed;
};

export default function DesktopStatsSection({ id = "achievements" }) {
  const { t } = useLang();
  const stats = t?.stats || null;

  const items = useMemo(() => (
    stats
      ? [
          { value: stats.payout, label: stats.publishers },
          { value: stats.mau, label: stats.mauLabel },
          { value: stats.adv, label: stats.advertisers }
        ]
      : []
  ), [
    stats?.payout,
    stats?.mau,
    stats?.adv,
    stats?.publishers,
    stats?.mauLabel,
    stats?.advertisers
  ]);

  const parsed = useMemo(
    () => items.map((it) => ({ ...it, ...parseStat(it.value) })),
    [items]
  );

  const [counts, setCounts] = useState(() => parsed.map(() => 0));

  useEffect(() => {
    if (!parsed.length) return;

    const prefersReduced = typeof window !== "undefined" && window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced) {
      setCounts(parsed.map((it) => it.numeric));
      return;
    }

    const duration = 2200;
    const start = performance.now();

    const easeOut = (t) => 1 - Math.pow(1 - t, 3);

    let raf = null;
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = easeOut(progress);
      setCounts(parsed.map((it) => it.numeric * eased));
      if (progress < 1) raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => raf && cancelAnimationFrame(raf);
  }, [parsed]);

  if (!stats) return null;

  return (
    <section id={id} className="stats-root stats-desktop" aria-label="Achievements">
      <div className="stats-wrap">
        <p className="stats-eyebrow">{stats.eyebrow}</p>
        <h2 className="stats-title">
          {stats.titleLine1}<br/>{stats.titleLine2}
        </h2>

        <ul className="stats-grid">
          {parsed.map((it, i) => (
            <li key={i} className="stat">
              <div className="stat-value" aria-hidden="true">
                {`${it.prefix}${formatCount(counts[i] || 0, it.decimals)}${it.unit}${it.hasPlus ? "+" : ""}`}
              </div>
              <div className="stat-label">{it.label}</div>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
